import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { useEvents } from '../api';
import type { RoadmapEvent } from '../interfaces/event';
import { isAdvisoryNeedTask, isLifeEvent, isMaturity } from '../utils/eventUtils';

const matchesEventId = (event: RoadmapEvent, eventId: string | null): boolean => {
    if (!eventId) return false;

    if (event.key === eventId) return true;

    if (isMaturity(event)) {
        return event.qualifiedKey === eventId;
    }

    if (isLifeEvent(event) || isAdvisoryNeedTask(event)) {
        return event.id?.toString() === eventId;
    }

    return false;
};

export const useActiveEvent = (): [activeEvent: RoadmapEvent | undefined, setActiveEvent: (event: RoadmapEvent | undefined) => void] => {
    const {
        data: { lifeEvents, maturities, advisoryNeedTasks },
    } = useEvents();
    const [searchParams, setSearchParams] = useSearchParams();
    const eventId = searchParams.get('eventId');

    const activeEvent = useMemo(() => {
        return [...lifeEvents, ...maturities, ...advisoryNeedTasks].find((event) => matchesEventId(event, eventId));
    }, [lifeEvents, maturities, advisoryNeedTasks, eventId]);

    const setActiveEvent = useCallback(
        (event: RoadmapEvent | undefined) => {
            if (!event) {
                setSearchParams(new URLSearchParams());

                return;
            }

            if (isMaturity(event) && event.qualifiedKey) {
                setSearchParams({ eventId: event.qualifiedKey });
            } else if (event.key) {
                setSearchParams({ eventId: event.key });
            } else if ((isLifeEvent(event) || isAdvisoryNeedTask(event)) && event.id) {
                setSearchParams({ eventId: event.id.toString() });
            } else {
                setSearchParams(new URLSearchParams());
            }
        },
        [setSearchParams],
    );

    return [activeEvent, setActiveEvent];
};
